import { useCallback } from 'react'
import { useLoading } from 'shared/hooks'
import { useTags } from 'modulesCommon/Tags/useTags'
import { useUserTagFollows } from 'modulesCommon/Tags/userTagFollows'
import { useUnfollowTagMutation } from 'modulesCommon/Tags/graphql/unfollowTag.types'
import { useFollowTagMutation } from 'modulesCommon/Tags/graphql/followTag.types'
import { GetUserTagFollowsDocument } from 'modulesCommon/Tags/graphql/userTagFollows.types'

import { Tag, TagFollow } from 'shared/__gql-types'

export const useFollowedTags = ({ zoneId, spaceId }: { zoneId: string; spaceId: string; }) => {
  const { tags, loading: tagsLoading } = useTags({ labelId: zoneId, spaceId })
  const { isTagFollowed, loading: userFollowTagsLoading } = useUserTagFollows()

  const [unfollowTagRequest, { loading: unfollowTagLoading }] = useUnfollowTagMutation({
    update(cache, { data }, { variables }) {
      if (typeof data?.unfollowTag !== 'undefined') {
        const res = cache.readQuery<{ getUserTagFollows: TagFollow[] }>({
          query: GetUserTagFollowsDocument
        })

        cache.writeQuery({
          query: GetUserTagFollowsDocument,
          data: {
            getUserTagFollows: res?.getUserTagFollows?.filter((tag) => tag.tag_id !== variables?.tag_id)
          }
        })
      }
    }
  })
  const [followTagRequest, { loading: followTagLoading }] = useFollowTagMutation({
    update(cache, { data }) {
      if (typeof data?.followTag !== 'undefined') {
        const res = cache.readQuery<{ getUserTagFollows: TagFollow[] }>({
          query: GetUserTagFollowsDocument
        })

        cache.writeQuery({
          query: GetUserTagFollowsDocument,
          data: {
            getUserTagFollows: [...res?.getUserTagFollows ?? [], data.followTag],
          }
        })
      }
    }
  })

  const followTag = useCallback(
    (tag_id) =>
      followTagRequest({
        variables: { tag_id }
      }),
    [followTagRequest]
  )

  const unfollowTag = useCallback(
    (tag_id) =>
      unfollowTagRequest({
        variables: { tag_id }
      }),
    [unfollowTagRequest]
  )

  const onToggle = (tag: Tag) => isTagFollowed(tag.id) ? unfollowTag(tag.id) : followTag(tag.id)

  useLoading({ isLoading: tagsLoading || userFollowTagsLoading || unfollowTagLoading || followTagLoading })

  return {
    onToggle,
    followTag,
    unfollowTag,
    zoneTags: tags,
    isTagFollowed
  }
}
